import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('theme') : null;
    if (saved) {
      return saved === 'dark';
    }
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const toggleTheme = () => {
    setIsDark(prev => !prev);
  };

  const theme = {
    isDark,
    toggleTheme,
    colors: {
      light: {
        primary: '#0d9488',   // teal-600
        secondary: '#115e59', // teal-800
        accent: '#14b8a6',    // teal-500
        background: '#fafafa', // neutral-50
        surface: '#ffffff',
        surfaceHover: '#f5f5f5', // neutral-100
        text: '#171717',      // neutral-900
        textSecondary: '#525252', // neutral-600
        textMuted: '#737373', // neutral-500
        border: '#e5e5e5',    // neutral-200
        borderHover: '#d4d4d4', // neutral-300
        shadow: 'rgba(0, 0, 0, 0.06)',
        shadowLg: 'rgba(0, 0, 0, 0.1)',
      },
      dark: {
        primary: '#2dd4bf',   // teal-400
        secondary: '#5eead4', // teal-300
        accent: '#14b8a6',    // teal-500
        background: '#0a0a0a', // neutral-950
        surface: '#171717',   // neutral-900
        surfaceHover: '#262626', // neutral-800
        text: '#fafafa',      // neutral-50
        textSecondary: '#d4d4d4', // neutral-300
        textMuted: '#a3a3a3', // neutral-400
        border: '#262626',    // neutral-800
        borderHover: '#404040', // neutral-700
        shadow: 'rgba(0, 0, 0, 0.3)',
        shadowLg: 'rgba(0, 0, 0, 0.5)',
      }
    },
    gradients: {
      light: {
        primary: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
        accent: 'linear-gradient(135deg, #14b8a6 0%, #2dd4bf 100%)',
        surface: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)',
      },
      dark: {
        primary: 'linear-gradient(135deg, #14b8a6 0%, #2dd4bf 100%)',
        accent: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
        surface: 'linear-gradient(135deg, rgba(23,23,23,0.8) 0%, rgba(23,23,23,0.4) 100%)',
      }
    }
  };

  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
};